import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Building2,
  CreditCard,
  Smartphone,
  PiggyBank,
  ArrowRight,
  CheckCircle2,
  Menu,
  X,
  Zap,
  Shield,
  Users,
  TrendingUp,
  Clock,
  Headphones,
  QrCode,
  FileText,
  Wallet,
  BarChart3,
  MessageCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_digital-bank-builder/artifacts/yj9xruy7_WhatsApp%20Image%202026-03-17%20at%2012.24.28.jpeg";
const WHATSAPP_LINK = "https://wa.me/5511921780586?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20como%20criar%20minha%20Fintech.";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-lg border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" className="flex items-center gap-4" data-testid="logo">
            <img src={LOGO_URL} alt="hubaas" className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-contain shadow-lg shadow-cyan-500/20" />
            <span className="font-heading font-bold text-2xl md:text-3xl text-white tracking-tight">hubaas</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#solucoes" className="text-slate-300 hover:text-secondary font-medium transition-colors">
              Soluções
            </a>
            <a href="#beneficios" className="text-slate-300 hover:text-secondary font-medium transition-colors">
              Benefícios
            </a>
            <a href="#contato" className="text-slate-300 hover:text-secondary font-medium transition-colors">
              Contato
            </a>
            <Link to="/simulador-fintech">
              <Button variant="outline" className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-white" data-testid="nav-simulator-btn">
                Simulador de Fintech
              </Button>
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="nav-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-2" />
                Fale com um Especialista
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-white/10 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 animate-fade-in">
            <div className="flex flex-col gap-4">
              <a href="#solucoes" className="text-slate-300 hover:text-secondary font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Soluções
              </a>
              <a href="#beneficios" className="text-slate-300 hover:text-secondary font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Benefícios
              </a>
              <a href="#contato" className="text-slate-300 hover:text-secondary font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Contato
              </a>
              <Link to="/simulador-fintech" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full border-2 border-secondary text-secondary">
                  Simulador de Fintech
                </Button>
              </Link>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Fale com um Especialista
                </Button>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

const HeroSection = () => (
  <section className="pt-28 md:pt-36 pb-16 md:pb-24 px-4 sm:px-6 lg:px-8 hero-pattern" data-testid="hero-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-8 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/20 text-secondary text-sm font-semibold">
            <Zap className="w-4 h-4" />
            Indicadores de Negócios
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none text-white">
            Conectamos você à sua <span className="gradient-text">Fintech White Label</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl">
            Somos especialistas em conectar você aos melhores parceiros: instituições de pagamentos, OTC e Software Houses para construir sua Fintech com a sua marca.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg pulse-glow" data-testid="hero-whatsapp-btn">
                <MessageCircle className="mr-2 w-5 h-5" />
                Fale com um Especialista
              </Button>
            </a>
            <Link to="/simulador-viabilidade">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white hover:border-secondary hover:text-secondary px-8 py-6 text-lg" data-testid="hero-viability-btn">
                Calcular Viabilidade
              </Button>
            </Link>
          </div>
          
          <div className="flex flex-wrap gap-6 pt-4">
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle2 className="w-5 h-5 text-accent" />
              <span>Consultoria gratuita</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle2 className="w-5 h-5 text-accent" />
              <span>Parceiros certificados</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle2 className="w-5 h-5 text-accent" />
              <span>Suporte dedicado</span>
            </div>
          </div>
        </div>
        
        <div className="relative lg:pl-12 animate-fade-in animation-delay-200">
          <div className="phone-mockup max-w-[320px] mx-auto">
            <div className="phone-screen">
              <div className="hubaas-gradient p-6 text-white">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm opacity-80">Saldo disponível</p>
                    <p className="text-2xl font-bold">R$ 12.450,00</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5" />
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-4 bg-zinc-900">
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { icon: QrCode, label: "PIX" },
                    { icon: FileText, label: "Boleto" },
                    { icon: CreditCard, label: "Cartão" },
                    { icon: TrendingUp, label: "Invest" },
                  ].map((item, i) => (
                    <div key={i} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer transition-colors">
                      <item.icon className="w-5 h-5 text-secondary" />
                      <span className="text-xs text-slate-400">{item.label}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-white">Últimas transações</p>
                  {[
                    { name: "PIX Recebido", value: "+ R$ 1.500,00", color: "text-accent" },
                    { name: "Pagamento Boleto", value: "- R$ 250,00", color: "text-slate-400" },
                    { name: "Compra Cartão", value: "- R$ 89,90", color: "text-slate-400" },
                  ].map((tx, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-white/10">
                      <span className="text-sm text-slate-400">{tx.name}</span>
                      <span className={`text-sm font-medium ${tx.color}`}>{tx.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Floating stats */}
          <div className="absolute -left-4 top-1/4 glass rounded-xl p-4 hidden lg:block animate-fade-in animation-delay-300">
            <p className="text-2xl font-bold text-secondary">+50</p>
            <p className="text-sm text-slate-400">Parceiros</p>
          </div>
          
          <div className="absolute -right-4 bottom-1/4 glass rounded-xl p-4 hidden lg:block animate-fade-in animation-delay-400">
            <p className="text-2xl font-bold text-accent">+200</p>
            <p className="text-sm text-slate-400">Fintechs criadas</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const products = [
  {
    icon: Building2,
    title: "Instituições de Pagamento",
    description: "Conectamos você às melhores IPs do mercado para processar pagamentos, emitir cartões e oferecer contas digitais.",
    features: ["Licenças Reguladas", "Compliance", "Infraestrutura"],
    color: "bg-cyan-500",
  },
  {
    icon: PiggyBank,
    title: "SCD - Crédito Direto",
    description: "Conexão com SCDs para estruturar operações de crédito, emissão de CCB e antecipação de recebíveis com segurança regulatória.",
    features: ["Emissão de CCB", "Antecipação", "Registro B3"],
    color: "bg-purple-600",
  },
  {
    icon: Wallet,
    title: "Conta Escrow Estruturada",
    description: "Estrutura neutra e segura para operações de crédito, trava de recebíveis e garantias com SCDs parceiras.",
    features: ["Trava Recebíveis", "Garantias", "Conciliação"],
    color: "bg-teal-500",
  },
  {
    icon: QrCode,
    title: "PIX & Meios de Pagamento",
    description: "Parceiros especializados em PIX, TED, boletos e processamento de pagamentos com as melhores taxas.",
    features: ["PIX Instantâneo", "Boletos", "Gateway"],
    color: "bg-emerald-500",
  },
  {
    icon: CreditCard,
    title: "Emissores de Cartões",
    description: "Encontre o parceiro ideal para emitir cartões de crédito e débito com a sua marca.",
    features: ["White Label", "Bandeiras", "Benefícios"],
    color: "bg-amber-500",
  },
  {
    icon: BarChart3,
    title: "OTC & Software Houses",
    description: "Parceiros de câmbio, cripto e desenvolvedores especializados em plataformas Fintech completas.",
    features: ["Câmbio", "App Mobile", "APIs"],
    color: "bg-orange-500",
  },
];

const ProductsSection = () => (
  <section id="solucoes" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-zinc-900/50" data-testid="products-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
        <span className="text-secondary font-semibold text-sm tracking-wider uppercase">Nossas Conexões</span>
        <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mt-4 mb-6">
          Conectamos você aos melhores parceiros
        </h2>
        <p className="text-lg text-slate-400">
          Somos especialistas em identificar e conectar você às melhores soluções para criar sua Fintech.
        </p>
      </div>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product, index) => (
          <Card 
            key={index} 
            className="card-hover bg-zinc-800/50 border-white/10 overflow-hidden"
            data-testid={`product-card-${index}`}
          >
            <CardContent className="p-6 md:p-8">
              <div className={`w-14 h-14 rounded-2xl ${product.color} flex items-center justify-center mb-6`}>
                <product.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">{product.title}</h3>
              <p className="text-slate-400 mb-6 leading-relaxed">{product.description}</p>
              <div className="flex flex-wrap gap-2">
                {product.features.map((feature, i) => (
                  <span key={i} className="px-3 py-1 rounded-full bg-white/10 text-slate-300 text-sm font-medium">
                    {feature}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

const benefits = [
  {
    icon: Zap,
    title: "Conexões Estratégicas",
    description: "Acesso aos melhores parceiros do mercado financeiro.",
  },
  {
    icon: Clock,
    title: "Economia de Tempo",
    description: "Reduzimos seu tempo de pesquisa e negociação.",
  },
  {
    icon: Shield,
    title: "Parceiros Certificados",
    description: "Trabalhamos apenas com empresas reguladas e confiáveis.",
  },
  {
    icon: Headphones,
    title: "Suporte Dedicado",
    description: "Acompanhamento em todas as etapas do processo.",
  },
  {
    icon: Users,
    title: "Rede de Especialistas",
    description: "Equipe com experiência no mercado financeiro.",
  },
  {
    icon: TrendingUp,
    title: "Melhores Condições",
    description: "Negociamos as melhores taxas e condições para você.",
  },
];

const BenefitsSection = () => (
  <section id="beneficios" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-testid="benefits-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-secondary font-semibold text-sm tracking-wider uppercase">Por que nos escolher</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mt-4 mb-6">
            Sua Fintech começa com a conexão certa
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Conectamos você aos parceiros ideais para transformar sua visão em uma Fintech de sucesso.
          </p>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4" data-testid={`benefit-item-${index}`}>
                <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white mb-1">{benefit.title}</h3>
                  <p className="text-sm text-slate-400">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="relative">
          <div className="credit-card w-full max-w-md mx-auto hubaas-gradient">
            <div className="flex justify-between items-start mb-8">
              <div className="w-12 h-10 rounded bg-gradient-to-br from-yellow-400 to-yellow-600" />
              <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="text-white/60 text-sm mb-2 font-mono">Número do Cartão</div>
            <div className="text-white text-xl font-mono tracking-widest mb-6">•••• •••• •••• 4242</div>
            <div className="flex justify-between">
              <div>
                <div className="text-white/60 text-xs mb-1">TITULAR</div>
                <div className="text-white font-medium">SUA FINTECH</div>
              </div>
              <div>
                <div className="text-white/60 text-xs mb-1">VALIDADE</div>
                <div className="text-white font-medium">12/28</div>
              </div>
            </div>
          </div>
          
          <div className="absolute -bottom-6 -right-6 glass rounded-xl p-4 hidden lg:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="font-semibold text-white">Parceiro conectado</p>
                <p className="text-sm text-slate-400">White label pronto</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-zinc-900" data-testid="cta-section">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">
        Pronto para criar sua Fintech?
      </h2>
      <p className="text-lg md:text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
        Fale com nossos especialistas e descubra como conectamos você aos melhores parceiros do mercado.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
          <Button size="lg" className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg" data-testid="cta-whatsapp-btn">
            <MessageCircle className="mr-2 w-5 h-5" />
            Fale com um Especialista
          </Button>
        </a>
        <Link to="/simulador-viabilidade">
          <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg" data-testid="cta-viability-btn">
            <TrendingUp className="mr-2 w-5 h-5" />
            Calcular Viabilidade
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

const ContactSection = () => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    empresa: "",
    mensagem: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Mensagem enviada com sucesso! Entraremos em contato em breve.");
      setFormData({ nome: "", email: "", telefone: "", empresa: "", mensagem: "" });
    } catch (error) {
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-zinc-900/50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <span className="text-secondary font-semibold text-sm tracking-wider uppercase">Fale Conosco</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mt-4 mb-6">
              Vamos conectar você ao parceiro ideal
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Nossa equipe de especialistas está pronta para encontrar a melhor solução para sua Fintech.
            </p>
            
            <div className="space-y-6">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 rounded-xl bg-green-600/20 border border-green-600/30 hover:bg-green-600/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">WhatsApp</p>
                  <p className="text-slate-400">+55 11 92178-0586</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Resposta Rápida</p>
                  <p className="text-slate-400">Em até 24 horas úteis</p>
                </div>
              </div>
            </div>
          </div>
          
          <Card className="bg-zinc-800/50 border-white/10">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Nome *</label>
                    <Input
                      required
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="Seu nome"
                      className="bg-zinc-900/50 border-white/10 text-white placeholder:text-slate-500 focus:bg-zinc-900"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                    <Input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="seu@email.com"
                      className="bg-zinc-900/50 border-white/10 text-white placeholder:text-slate-500 focus:bg-zinc-900"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Telefone</label>
                    <Input
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      placeholder="(11) 99999-9999"
                      className="bg-zinc-900/50 border-white/10 text-white placeholder:text-slate-500 focus:bg-zinc-900"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Empresa</label>
                    <Input
                      value={formData.empresa}
                      onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                      placeholder="Nome da empresa"
                      className="bg-zinc-900/50 border-white/10 text-white placeholder:text-slate-500 focus:bg-zinc-900"
                      data-testid="contact-company"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Mensagem *</label>
                  <Textarea
                    required
                    value={formData.mensagem}
                    onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                    placeholder="Conte-nos sobre seu projeto de Fintech..."
                    rows={4}
                    className="bg-zinc-900/50 border-white/10 text-white placeholder:text-slate-500 focus:bg-zinc-900 resize-none"
                    data-testid="contact-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full hubaas-gradient text-white py-6"
                  data-testid="contact-submit"
                >
                  {loading ? "Enviando..." : "Enviar Mensagem"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-black text-white" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="hubaas" className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-contain shadow-lg shadow-cyan-500/20" />
          <span className="font-heading font-bold text-2xl md:text-3xl">hubaas</span>
        </div>
        
        <div className="flex flex-wrap justify-center gap-6 text-slate-400">
          <a href="#solucoes" className="hover:text-white transition-colors">Soluções</a>
          <a href="#beneficios" className="hover:text-white transition-colors">Benefícios</a>
          <a href="#contato" className="hover:text-white transition-colors">Contato</a>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-green-500 transition-colors flex items-center gap-1">
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
        </div>
        
        <p className="text-slate-500 text-sm">
          © 2024 hubaas. Todos os direitos reservados.
        </p>
      </div>
    </div>
  </footer>
);

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <ProductsSection />
        <BenefitsSection />
        <CTASection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
